import { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Center, Spinner } from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import { ClipboardIconButton, ClipboardInput, ClipboardRoot } from '@/components/ui/clipboard';
import { InputGroup } from '@/components/ui/input-group';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import api from '@/utils/api';
import { buildPublicUrl } from '@/utils/public-url';
import { CollectionType, DailogProps } from '@/lib/types';

interface Props extends DailogProps {
  item: CollectionType;
  reloadContents(): void;
}

export default function ShareLink({ isOpen, onClose, item, reloadContents }: Props) {
  const [link, setLink] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const hasFetchedLink = useRef(false);
  const safeToUpdate = useRef(false);

  const safeStateUpdate = useCallback((callback: () => void) => {
    requestAnimationFrame(() => {
      if (safeToUpdate.current) {
        callback();
      }
    });
  }, []);

  const generateLink = useCallback(
    async (item: CollectionType) => {
      safeStateUpdate(() => {
        setIsLoading(true);
      });

      try {
        const promise = api.patch(`collection-types/public-link/${item.id}`, {});
        toaster.promise(promise, {
          success: {
            title: `Link was generated successfully`,
            description: '',
          },
          error: {},
          loading: { title: 'Generating link...', description: 'Please wait' },
        });

        const response = await promise;
        const generatedLink = response.data.public_link.data.tiny_url || '';
        const displayLink =
          buildPublicUrl('collection-types', item.reference_id) || generatedLink;
        safeStateUpdate(() => {
          setLink(displayLink);
          setIsLoading(false);
        });

        setTimeout(() => {
          reloadContents();
        }, 100);
      } catch (err: any) {
        const errorMsg = err.response?.data?.message || 'Failed to generate public link';

        safeStateUpdate(() => {
          setIsLoading(false);
        });

        toaster.create({
          type: 'error',
          title: errorMsg,
        });

        return '';
      }
    },
    [reloadContents, safeStateUpdate],
  );

  useEffect(() => {
    safeToUpdate.current = true;

    if (!isOpen) {
      hasFetchedLink.current = false;
      return;
    }

    const initTimeout = setTimeout(() => {
      if (isOpen && !hasFetchedLink.current) {
        const fetchLink = async () => {
          if (item.public_link) {
            setLink(
              buildPublicUrl('collection-types', item.reference_id) || item.public_link,
            );
          } else {
            await generateLink(item);
          }
          hasFetchedLink.current = true;
        };

        fetchLink();
      }
    }, 100);

    return () => {
      clearTimeout(initTimeout);
      safeToUpdate.current = false;
    };
  }, [isOpen, item, generateLink]);

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={onClose}>
      <DialogBackdrop />
      <DialogTrigger />
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Copy the generated link:</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Box>
            {isLoading ? (
              <Center mt={4}>
                <Spinner size={'lg'} />
              </Center>
            ) : (
              <Box>
                <ClipboardRoot maxW='full' value={link}>
                  <InputGroup width='full' endElement={<ClipboardIconButton me='-2' />}>
                    <ClipboardInput />
                  </InputGroup>
                </ClipboardRoot>
              </Box>
            )}
          </Box>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
